import React from 'react';

import styled from 'styled-components';

import Button from 'components/lib/Button';
import Modal from 'components/lib/Modal';

import ShopRequestValidationSection from './ShopRequestValidationSection';
import type { ShopRequestValidationWarning } from '../types';

export type { ShopRequestValidationWarning } from '../types';

const StyledModal = styled(Modal)`
  &&& .ant-modal-body {
    padding: 20px;
    max-height: 520px;
    overflow-y: auto;
  }

  &&& .ant-modal-body::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &&& .ant-modal-body::-webkit-scrollbar-track {
    border-radius: 100px;
    background-color: transparent;
  }

  &&& .ant-modal-body::-webkit-scrollbar-thumb {
    height: 100px;
    border-radius: 3px;
    background-color: #89919d;
  }

  &&& .ant-modal-body::-webkit-scrollbar-button {
    display: none;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Subtitle = styled.div`
  font-size: 14px;
  font-family: var(--font-regular) !important;
  font-weight: 400 !important;
  color: var(--text-secondary);
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

type Props = {
  open: boolean;
  warnings: ShopRequestValidationWarning[];
  onConfirm: () => void;
  onClose: () => void;
  confirmLoading?: boolean;
};

const ShopRequestValidationModal = ({ open, warnings, onConfirm, onClose, confirmLoading }: Props) => {
  const blockingItems = warnings.filter((warning) => Boolean(warning?.is_blocking));
  const nonBlockingItems = warnings.filter((warning) => !warning?.is_blocking);
  const hasBlockingItems = blockingItems.length > 0;

  return (
    <StyledModal
      data-test-id="sr-validation-modal"
      open={open}
      width={540}
      title={
        <TitleContainer data-test-id="sr-validation-modal-title-container">
          <div data-test-id="sr-validation-modal-title">Validation Rules</div>
          <Subtitle data-test-id="sr-validation-modal-subtitle">
            Review required actions and informational items
          </Subtitle>
        </TitleContainer>
      }
      footer={
        <>
          {!hasBlockingItems && (
            <Button
              type="primary"
              onClick={onConfirm}
              loading={confirmLoading}
              disabled={confirmLoading}
              data-test-id="sr-validation-modal-continue"
            >
              Continue anyway
            </Button>
          )}
          <Button onClick={onClose} disabled={confirmLoading} data-test-id="sr-validation-modal-close">
            Close
          </Button>
        </>
      }
      centered
      onCancel={onClose}
    >
      <Content data-test-id="sr-validation-modal-content">
        <ShopRequestValidationSection
          title="Actions Required"
          items={blockingItems}
          iconSrc="/icons/atoms-icons-dangerous.svg"
          iconBackgroundColor="#ffffff"
          itemBackgroundColor="#fa755026"
          note="The Shop Request cannot move to Needs Approval until Care Fee is provided."
          sectionTestId="sr-validation-actions-required"
        />
        <ShopRequestValidationSection
          title="Warnings"
          items={nonBlockingItems}
          iconSrc="/icons/info-blue.svg"
          iconBackgroundColor="rgba(0, 122, 255, 0.1);"
          note="Note: Matching the prior period is not mandatory."
          sectionTestId="sr-validation-warnings"
        />
      </Content>
    </StyledModal>
  );
};

export default ShopRequestValidationModal;
